import React from 'react';
import {Navigate} from 'react-router-dom';
import {useAuth} from '../contexts/PhpAuthContext';

const ProtectedRoute = ({children, allowedRoles = []}) => {
  const {profile, loading} = useAuth ();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!profile) {
    // Check which login page to redirect to based on the route
    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/patient/')) {
      return <Navigate to="/patient/login" replace />;
    }
    if (currentPath.startsWith('/admin/')) {
      return <Navigate to="/admin/login" replace />;
    }
    if (currentPath.startsWith('/clinic/')) {
      return <Navigate to="/login" replace />;
    }
    return <Navigate to="/login" replace />;
  }

  // Ensure profile.role is valid
  if (!profile.role) {
    console.error('ProtectedRoute: Profile exists but role is missing', profile);
    // Try to infer role or redirect to login
    if (profile.patient_unique_id) {
      // It's a patient but role is missing - set it
      profile.role = 'patient';
      console.log('ProtectedRoute: Inferred role as patient from patient_unique_id');
    } else {
      // Unknown role, redirect to login
      console.error('ProtectedRoute: Cannot determine role, redirecting to login');
      localStorage.removeItem('session_token');
      const currentPath = window.location.pathname;
      if (currentPath.startsWith('/patient/')) {
        return <Navigate to="/patient/login" replace />;
      }
      if (currentPath.startsWith('/admin/')) {
        return <Navigate to="/admin/login" replace />;
      }
      return <Navigate to="/login" replace />;
    }
  }
  
  // Log current route and role for debugging
  console.log('ProtectedRoute: Current path:', window.location.pathname, 'User role:', profile.role, 'Allowed roles:', allowedRoles);

  if (allowedRoles.length > 0 && !allowedRoles.includes (profile.role)) {
    console.log('ProtectedRoute: User role', profile.role, 'not allowed. Allowed roles:', allowedRoles, 'Current path:', window.location.pathname);
    // Redirect based on role if they land somewhere they shouldn't
    switch (profile.role) {
      case 'patient':
        console.log('ProtectedRoute: Redirecting patient to /patient/dashboard');
        return <Navigate to="/patient/dashboard" replace />;
      case 'doctor':
      case 'chief_doctor':
        console.log('ProtectedRoute: Redirecting doctor to /doctor/dashboard');
        return <Navigate to="/doctor/dashboard" replace />;
      case 'admin':
        console.log('ProtectedRoute: Redirecting admin to /admin/dashboard');
        return <Navigate to="/admin/dashboard" replace />;
      case 'clinic_login':
        console.log('ProtectedRoute: Redirecting clinic_login to /clinic/dashboard');
        return <Navigate to="/clinic/dashboard" replace />;
      default:
        console.warn('ProtectedRoute: Unknown role, redirecting to home');
        return <Navigate to="/" replace />;
    }
  }
  
  // Additional check: If we're on a patient route but role is not patient, redirect
  const currentPath = window.location.pathname;
  if (currentPath.startsWith('/patient/') && profile.role !== 'patient') {
    console.error('ProtectedRoute: On patient route but role is', profile.role, '- redirecting to appropriate dashboard');
    switch (profile.role) {
      case 'doctor':
      case 'chief_doctor':
        return <Navigate to="/doctor/dashboard" replace />;
      case 'admin':
        return <Navigate to="/admin/dashboard" replace />;
      default:
        return <Navigate to="/" replace />;
    }
  }
  
  // Additional check: If we're on a doctor route but role is patient, redirect
  if (currentPath.startsWith('/doctor/') && profile.role === 'patient') {
    console.error('ProtectedRoute: On doctor route but role is patient - redirecting to patient dashboard');
    return <Navigate to="/patient/dashboard" replace />;
  }

  return children;
};

export default ProtectedRoute;
